// Canvas helpers for cover-image cropping. `getCroppedCoverBlob` rasterizes the
// chosen crop region into a fixed square (COVER_SIZE) JPEG blob, ready to upload.
// Kept framework-free so it can be reused by any picker UI.

import type { Area } from 'react-easy-crop';
import { COVER_SIZE } from '../domain';

/** Load an image element from a URL (e.g. an object URL), resolving once decoded. */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Failed to load image.')));
    image.src = src;
  });
}

/** Natural pixel dimensions of the image at `src`. */
export async function imageDimensions(
  src: string,
): Promise<{ width: number; height: number }> {
  const image = await loadImage(src);
  return { width: image.naturalWidth, height: image.naturalHeight };
}

/**
 * Render the selected crop region of `src` into a square `size`×`size` JPEG blob.
 * `crop` is in source-image pixels (as reported by react-easy-crop).
 */
export async function getCroppedCoverBlob(
  src: string,
  crop: Area,
  size: number = COVER_SIZE,
): Promise<Blob> {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is unavailable.');
  }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    size,
    size,
  );
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image.'))),
      'image/jpeg',
      0.9,
    );
  });
}
